import React, { useEffect, useState, useCallback, useMemo } from 'react';
import ModalWrapper from '../ModalWrapper/ModalWrapper';
import { Box, Button, TextField, Typography, Snackbar, Alert, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { MuiTelInput } from 'mui-tel-input';
import Swal from 'sweetalert2';
import { useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../context/authContext';
import { useSite } from "../../context/siteContext";
import { contactCCR } from '../../services/contactCCRFormService';
import { getMachineByNumberOrId } from "../../services/myMachineService";
import { useOptions } from "../../context/optionContext";
import { REGION_PHONE_NUMBERS } from '../Layout2/Footer/Footer';
import { resolvePhoneState } from '../../utilities/phoneUtils';
import PhoneInput from '../PhoneInput/PhoneInput';
import { resolveEmergencyNumberForRegion, resolveSupportSettings, SUPPORT_DEFAULT_REGION_PHONES } from '../../utilities/supportSettings';

const EMPTY_FORM = {
	name: '',
	email: '',
	phone: '',
	machine_number: '',
	subject: '',
	message: '',
};


const parseEmergencyPhones = (raw) => {
	if (!raw) return null;
	try {
		const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
		if (!Array.isArray(parsed)) return null;
		return parsed
			.filter((row) => row && row.region)
			.map((row) => ({ region: row.region, phone: row.phone || "" }));
	} catch {
		return null;
	}
};

const ContactCCRForm = ({
	open,
	setOpen,
	machine,
	prefill,
	mode = 'ccr',
	showEmergencyNotice = true,
}) => {
	const { t } = useTranslation();
	const { user } = useAuth();
	const isNameLocked = !!user?.user_display_name;
	const isEmailLocked = !!user?.user_email;
	const isPhoneLocked = !!user?.user_phone;
	const { selectedSite, selectedSiteData } = useSite();
	const { optionKeys } = useOptions();
	const location = useLocation();

	const [errors, setErrors] = useState({});
	const [formData, setFormData] = useState(EMPTY_FORM);
	const [routeMachine, setRouteMachine] = useState(null);

	const [successOpen, setSuccessOpen] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	// Premium mode specific state
	const [premiumOption, setPremiumOption] = useState('');
	const [optionalComment, setOptionalComment] = useState('');
	const isPremium = mode === "premium";

	// auto-adjust message when the premium dropdown changes
	useEffect(() => {
		if (!isPremium) return;
		if (premiumOption === 'learn_more_about_premium') {
			setFormData((p) => ({
				...p,
				message:
					t('PREMIUM_ACCESS.PREMIUM_MESSAGES.LEARN_MORE') ||
					'I would like to learn more about the AMF Premium program and pricing. Please contact me.',
			}));
		} else if (premiumOption === 'request_related') {
			setFormData((p) => ({
				...p,
				message: '',
			}));
		} else {
			// for any other premium choice we want a general access request message
			setFormData((p) => ({
				...p,
				message:
					t('PREMIUM_ACCESS.PREMIUM_MESSAGES.REQUEST_ACCESS') ||
					'I would like to request access to AMF Premium training content. Please provide more information about the available training programs and pricing.',
			}));
		}
	}, [premiumOption, t, isPremium]);

	const emergencyPhone = useMemo(() => {
    const supportSettings = resolveSupportSettings(optionKeys || {});
    if (!supportSettings.contactCcrShowEmergencyMessage) return null;

    const configured = parseEmergencyPhones(
        optionKeys?.customerLocationEmergencyPhones,
        SUPPORT_DEFAULT_REGION_PHONES
    );

    return resolveEmergencyNumberForRegion({
        source: supportSettings.contactCcrEmergencyNumberSource,
        manualNumber: supportSettings.contactCcrEmergencyNumberManual,
        userRegion: user?.region,
        phones: configured,
    });
}, [user?.region, optionKeys]);


	const modalTitle = isPremium
		? "AMF Premium Request Form"
		: "Contact CCR";

	const modalSubtitle = isPremium
		? "Please fill out the form below to request AMF Premium training access"
		: "Please fill out the form below and the CCR team will reach out to you";

	const successMessage = isPremium
		? "Your Premium access request has been sent successfully. Our training team will contact you soon."
		: "Your message has been sent successfully. The CCR team will reach out to you soon.";

	const errorMessage = isPremium
		? "Something went wrong while sending your Premium request. Please try again."
		: "Something went wrong while sending your message. Please try again.";

	const premiumSelectMenuProps = {
		// Render with a modal-specific class and ensure the popover anchors
		// below the input so options appear where expected inside modals.
		anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
		transformOrigin: { vertical: 'top', horizontal: 'left' },
		slotProps: {
			root: { className: "modal-menu-popover" },
		},
		PaperProps: { style: { maxHeight: 320 } },
	};



	const buildInitialForm = useCallback(() => {
		const resolvedMachine = machine || routeMachine;
		const phoneState = resolvePhoneState({
			phoneValue: user?.user_phone || "",
			countryLabel: user?.country || user?.user_meta?.country || "",
			phoneCountryIso2: user?.phone_country_iso2 || user?.user_meta?.phone_country_iso2 || "",
			phoneCountryCode: user?.phone_country_code || user?.user_meta?.phone_country_code || "",
		});
		const base = {
			name: user?.user_display_name || '',
			phone: phoneState.phoneValue,
			phone_country_iso2: phoneState.meta.phone_country_iso2,
			phone_country_code: phoneState.meta.phone_country_code,
			email: user?.user_email || '',
		};

		const machineExtras = resolvedMachine
			? {
				subject: `${t('UI_TEXT.PARTS_FOR')} ${resolvedMachine?.machine_name} - ${resolvedMachine?.machine_number} ${t('UI_TEXT.ARE_NOT_AVAILABLE')}`,
				machine_number: resolvedMachine?.machine_number || '',
				message: `${t('UI_TEXT.I_WOULD_LIKE_TO_ORDER_A_PART_FOR_MY_MACHINE')} - ${resolvedMachine?.machine_name} - ${resolvedMachine?.machine_number} ${t('UI_TEXT.BUT_THE_PARTS_ARE_NOT_AVAILABLE_PLEASE_SEND_THE_PARTS_LIST_IF_POSSIBLE')}`,
			}
			: {};

		const override = prefill && typeof prefill === "object" ? prefill : {};

		return {
			...EMPTY_FORM,
			...base,
			...machineExtras,
			...override,
			site_number: selectedSite,
			site_id: selectedSiteData?.site_id || null,
		};
	}, [user, machine, routeMachine, prefill, selectedSite, selectedSiteData?.site_id]);

	useEffect(() => {
		if (!open) {
			setRouteMachine(null);
			return;
		}
		if (machine) return;
		if (!location.pathname.includes("/machines")) {
			setRouteMachine(null);
			return;
		}
		if (!selectedSiteData?.site_id) return;

		const segments = location.pathname.split("/").filter(Boolean);
		const machineToken = segments[1];
		if (!machineToken) return;

		const machineId = Number(machineToken);
		if (!Number.isFinite(machineId)) return;

		const fetchMachine = async () => {
			try {
				const response = await getMachineByNumberOrId({
					machine_id: machineId,
					site_id: selectedSiteData.site_id,
				});
				if (response?.status && Array.isArray(response?.data) && response.data.length > 0) {
					setRouteMachine(response.data[0]);
				}
			} catch (err) {
				console.warn("Failed to load machine for CCR form:", err?.message);
			}
		};

		fetchMachine();
	}, [open, machine, location.pathname, selectedSiteData?.site_id]);

	// Reset form + errors every time the modal opens (and also when it closes)
	useEffect(() => {
		if (open) {
			setErrors({});
			setFormData(buildInitialForm());
		} else {
			// clear out any leftover validation state so it doesn't flash next open
			setErrors({});
			setFormData(EMPTY_FORM);
		}
	}, [open, buildInitialForm]);

	const clearFieldError = (field) => {
		setErrors((prev) => {
			if (!prev[field]) return prev;
			const next = { ...prev };
			delete next[field];
			return next;
		});
	};

	const validate = () => {
		const e = {};

		if (!formData.name?.trim()) e.name = t('UI_TEXT.NAME_REQUIRED');

		if (!formData.email?.trim()) {
			e.email = t('ALERT_MESSAGES.EMAIL_IS_REQUIRED');
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
			e.email = t('ALERT_MESSAGES.INVALID_EMAIL_FORMAT');
		}

		if (!formData.phone?.trim()) e.phone = t('UI_TEXT.PHONE_IS_REQUIRED');

		// Only required for CCR
		if (!isPremium && !formData.machine_number?.trim()) {
			e.machine_number = 'Machine Number is required';
		}

		if (!formData.subject?.trim()) e.subject = t('UI_TEXT.SUBJECT_IS_REQUIRED');

		// Message is not required for schedule demo or request-related options
		if (
			!(isPremium && (premiumOption === 'schedule_demo' || premiumOption === 'request_related')) &&
			!formData.message?.trim()
		) {
			e.message = t('UI_TEXT.MESSAGE_IS_REQUIRED');
		}

		setErrors(e);
		return Object.keys(e).length === 0;
	};

	const onClose = () => setOpen(false);

	const onSubmit = async () => {
		if (!validate()) return;

		setSubmitting(true);
		try {
			// Prepare form data with premium-specific fields
			const submitData = {
				...formData,
				mode: isPremium ? "premium" : "ccr"
			};

			// Add premium-specific fields if in premium mode
			if (isPremium) {
				submitData.premium_option = premiumOption;
				submitData.optional_comment = optionalComment;
			}

			await contactCCR(submitData);

			// close modal first so swal feels like a post-submit confirmation
			setOpen(false);

			await Swal.fire({
				icon: 'success',
				title: 'Submitted!',
				text: successMessage,
				confirmButtonText: 'OK',
				allowOutsideClick: false,
			});

			// optional: reset (useEffect already resets on close, but this is safe)
			setErrors({});
			setFormData(EMPTY_FORM);
			setPremiumOption('');
			setOptionalComment('');
		} catch (err) {
			console.error(err);

			await Swal.fire({
				icon: 'error',
				title: 'Submission failed',
				text: errorMessage,
				confirmButtonText: 'OK',
			});
		} finally {
			setSubmitting(false);
		}
	};


	return (
		<>
			<ModalWrapper
				open={open}
				setOpen={setOpen}
				title={modalTitle}
				subtitle={modalSubtitle}
				content={
					<Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' ,overflowY: 'auto', maxHeight: '100%', pr: 1}}>
						<TextField
							label={t('TABLE_HEADERS.NAME')}
							fullWidth
							size="small"
							margin="normal"
							required
							disabled={isNameLocked}
							value={formData.name}
							error={Boolean(errors.name)}
							helperText={errors.name || ''}
							onChange={(e) => {
								clearFieldError('name');
								setFormData((p) => ({ ...p, name: e.target.value }));
							}}
						/>

						<TextField
							label={t('TABLE_HEADERS.EMAIL')}
							fullWidth
							size="small"
							margin="normal"
							required
							disabled={isEmailLocked}
							value={formData.email}
							error={Boolean(errors.email)}
							helperText={errors.email || ''}
							onChange={(e) => {
								clearFieldError('email');
								setFormData((p) => ({ ...p, email: e.target.value }));
							}}
						/>

						<PhoneInput
							value={formData.phone}
							onChange={(value) => {
								clearFieldError('phone');
								setFormData((p) => ({ ...p, phone: value }));
							}}
							countryIso2={formData.phone_country_iso2}
							countryCode={formData.phone_country_code}
							allowCountryChange={false}
							disabled={isPhoneLocked}
							label={t('LABELS.PHONE')}
							size="small"
							margin="normal"
							error={Boolean(errors.phone)}
							helperText={errors.phone || ''}
							onCountryChange={() => {}}
						/>

						{!isPremium && (
							<TextField
								label="Machine Number"
								fullWidth
								size="small"
								margin="normal"
								required
								value={formData.machine_number}
								error={Boolean(errors.machine_number)}
								helperText={errors.machine_number || ''}
								onChange={(e) => {
									clearFieldError('machine_number');
									setFormData((p) => ({ ...p, machine_number: e.target.value }));
								}}
							/>
						)}

						<TextField
							label={t('TABLE_HEADERS.SUBJECT')}
							fullWidth
							size="small"
							margin="normal"
							required
							value={formData.subject}
							error={Boolean(errors.subject)}
							helperText={errors.subject || ''}
							onChange={(e) => {
								clearFieldError('subject');
								setFormData((p) => ({ ...p, subject: e.target.value }));
							}}
						/>

						{isPremium && (
							<FormControl fullWidth size="small" margin="normal">
								<Select
									value={premiumOption || ''}
									onChange={(e) => setPremiumOption(e.target.value)}
									displayEmpty
									MenuProps={premiumSelectMenuProps}
									renderValue={(val) => {
										if (!val) return t('PREMIUM_ACCESS.PREMIUM_OPTIONS.PLACEHOLDER');
										const map = {
											schedule_demo: t('PREMIUM_ACCESS.PREMIUM_OPTIONS.SCHEDULE_DEMO'),
											learn_more_about_premium: t('PREMIUM_ACCESS.PREMIUM_OPTIONS.LEARN_MORE'),
											request_related: t('PREMIUM_ACCESS.PREMIUM_OPTIONS.HAVE_REQUEST') || 'I have a request related to AMF Premium',
											other: t('PREMIUM_ACCESS.PREMIUM_OPTIONS.OTHER'),
										};
										return map[val] || val;
									}}
								>
									{/* Placeholder */}
									<MenuItem value="" disabled>
										{t('PREMIUM_ACCESS.PREMIUM_OPTIONS.PLACEHOLDER')}
									</MenuItem>

									<MenuItem value="schedule_demo">
										{t('PREMIUM_ACCESS.PREMIUM_OPTIONS.SCHEDULE_DEMO')}
									</MenuItem>

									<MenuItem value="learn_more_about_premium">
										{t('PREMIUM_ACCESS.PREMIUM_OPTIONS.LEARN_MORE')}
									</MenuItem>

									<MenuItem value="request_related">
										{t('PREMIUM_ACCESS.PREMIUM_OPTIONS.HAVE_REQUEST') || 'I have a request related to AMF Premium'}
									</MenuItem>

									{/* <MenuItem value="other">
										{t('PREMIUM_ACCESS.PREMIUM_OPTIONS.OTHER')}
									</MenuItem> */}
								</Select>
							</FormControl>
						)}


						{isPremium && premiumOption === 'schedule_demo' && (
							<Box
								mt={2}
								mb={3}
								sx={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'flex-start',
									backgroundColor: 'background.paper',
									border: '1px solid',
									borderColor: 'divider',
									borderRadius: 2,
									p: 2,
									gap: 1
								}}
							>
								<Typography
									variant="subtitle2"
									color="textSecondary"
									fontWeight={600}
								>
									{t('PREMIUM_ACCESS.PREMIUM_OPTIONS.SCHEDULE_DEMO_INSTRUCTION') || 'Click here to schedule a demo:'}
								</Typography>
								<a
									href="https://outlook.office.com/book/AMFTraining1@amfbakery.onmicrosoft.com/?ismsaljsauthenabled"
									target="_blank"
									rel="noopener noreferrer"
									style={{
										color: '#1976d2',
										textDecoration: 'none',
										fontSize: '14px',
										fontWeight: '600',
										transition: 'color 0.2s ease',
										padding: '6px 12px',
										borderRadius: '4px',
										backgroundColor: 'rgba(25, 118, 210, 0.08)',
										border: '1px solid rgba(25, 118, 210, 0.2)',
										whiteSpace: 'nowrap'
									}}
									onMouseEnter={(e) => {
										e.target.style.color = '#1565c0';
										e.target.style.backgroundColor = 'rgba(25, 118, 210, 0.12)';
									}}
									onMouseLeave={(e) => {
										e.target.style.color = '#1976d2';
										e.target.style.backgroundColor = 'rgba(25, 118, 210, 0.08)';
									}}
								>
									{t('PREMIUM_ACCESS.PREMIUM_OPTIONS.SCHEDULE_DEMO')}
								</a>
								<Typography
									variant="caption"
									color="textSecondary"
									sx={{ mt: 1 }}
								>
									{t('PREMIUM_ACCESS.PREMIUM_OPTIONS.SCHEDULE_DEMO_SUBTEXT') || 'You will be redirected to our scheduling system in a new tab.'}
								</Typography>
							</Box>
						)}

						{isPremium && premiumOption === 'other' && (
							<TextField
								label={t('PREMIUM_ACCESS.PREMIUM_OPTIONS.COMMENTS_LABEL')}
								fullWidth
								multiline
								rows={3}
								size="small"
								margin="normal"
								value={optionalComment}
								onChange={(e) => setOptionalComment(e.target.value)}
								placeholder={t('PREMIUM_ACCESS.PREMIUM_OPTIONS.COMMENTS_PLACEHOLDER')}
							/>
						)}

						{!(isPremium && premiumOption === 'schedule_demo') && (
							<TextField
								label={t('TABLE_HEADERS.MESSAGE')}
								fullWidth
								multiline
								rows={4}
								size="small"
								margin="normal"
								required={!isPremium}
								value={formData.message}
								error={Boolean(errors.message)}
								helperText={errors.message || ''}
								onChange={(e) => {
									clearFieldError('message');
									setFormData((p) => ({ ...p, message: e.target.value }));
								}}
							/>
						)}

						{showEmergencyNotice && emergencyPhone && (
							<Typography variant="body1" fontSize="11px" sx={{ color: "error.main" }}>
								{t('UI_TEXT.IF_THIS_IS_AN_EMERGENCY_PLEASE_CALL_THE_EMERGENCY_NUMBER')}{" "}
								<a href={`tel:${emergencyPhone}`} style={{ color: "inherit", textDecoration: "underline", fontWeight: 600 }}>
									{emergencyPhone}
								</a>
							</Typography>
						)}
					</Box>
				}
				actions={
					<>
						<Button onClick={onClose} disabled={submitting}>
							{t('BUTTON.CANCEL')}
						</Button>
						{!(isPremium && premiumOption === 'schedule_demo') && (
							<Button color="primary" variant="contained" onClick={onSubmit} disabled={submitting}>
								{submitting ? 'Submitting…' : t('BUTTON.SUBMIT')}
							</Button>
						)}
					</>
				}
			/>

			<Snackbar
				open={successOpen}
				autoHideDuration={4000}
				onClose={() => setSuccessOpen(false)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert onClose={() => setSuccessOpen(false)} severity="success" variant="filled">
					{successMessage}
				</Alert>
			</Snackbar>
		</>
	);
};

export default ContactCCRForm;
